import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, BoxExtendedProps, Preloader, Text } from '@common-fe/common-fe';

import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { PaymentMethodVerificationStatus } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { CreateClaimDto, CreatedClaimDto, CreatedHsaWithdrawalClaimDto, CreateHsaWithdrawalClaimDto } from '@/modules/UseMyMoney/queries';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { useSubmitClaim, useValidateClaim } from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/hooks';

import AgreementButton from './AgreementButton';

interface Props extends BoxExtendedProps {
  createClaim: (claimDto: CreateClaimDto) => Promise<CreatedClaimDto>;
  createHsaWithdrawalClaim: (claimDto: CreateHsaWithdrawalClaimDto) => Promise<CreatedHsaWithdrawalClaimDto>;
  onUploadFile: () => void;
  setIsSuccessPopupVisible: (value: boolean) => void;
  isCreateClaimLoading?: boolean;
  isCreateHsaWithdrawalClaimLoading?: boolean;
  hasUndeliverableAddressError?: boolean;
}

const ClaimSummaryFooter: React.FC<Props> = ({
  createClaim,
  createHsaWithdrawalClaim,
  setIsSuccessPopupVisible,
  isCreateClaimLoading,
  isCreateHsaWithdrawalClaimLoading,
  hasUndeliverableAddressError,
  ...props
}) => {
  const { t } = useTranslation();
  const { handleSetFormValues, setValidationErrors } = useMyMoneyFormStore();

  const [hasAgreedToCertification, setHasAgreedToCertification] = useState(false);

  const activeMethod = useStore((state) => state.activeMethod);
  
  const isPaymentMethodVerificationFailed = useMemo(
    () =>
      activeMethod?.verificationStatus === PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
      activeMethod?.verificationStatus === PaymentMethodVerificationStatus.EXPIRED,
    [activeMethod]
  );

  const isSubmitDisabled = !hasAgreedToCertification || isPaymentMethodVerificationFailed;

  const onCreateClaim = useCallback((createdClaimId?: string) => {
    handleSetFormValues({ createdClaimId });
    setIsSuccessPopupVisible(true);
  }, [handleSetFormValues, setIsSuccessPopupVisible]);

  const {
    handleSubmit,
    createClaimDto,
    useMyMoneyOption,
  } = useSubmitClaim({ createClaim, onCreateClaim, createHsaWithdrawalClaim });
  const { validateClaim } = useValidateClaim();

  const claimValidation = useMemo(() => {
    return validateClaim(useMyMoneyOption, createClaimDto);
  }, [createClaimDto, useMyMoneyOption, validateClaim]);

  const isClaimValidationSuccessfully = useMemo(() => {
    return claimValidation?.length === 0;
  }, [claimValidation]);

  useEffect(() => {
    setValidationErrors(claimValidation);
  }, [claimValidation, setValidationErrors]);

  return (
    <Box align="center" data-testId="claim-summary-footer" {...props}>
      <Box
        pad="spacing16"
        border={{ size: 'small', color:'border1' }}
        round="container2Round"
      >
        <Box data-testId="claim-summary-footer_agreement" height={{ max: '84px' }} overflow="auto">
          <Text>
            {t(`I certify that the information submitted is accurate,
                complete and represents only eligible expenses under the
                rules of the plan(s) listed above.`)}
          </Text>
          <Text margin={{ top: 'spacing4' }}>
            {t(`I certify that these expenses have not previously been reimbursed
                (including through benefit debit card payment)
                and will not in the future be reimbursed by any other source or insurance.`)}
          </Text>
        </Box>

        <AgreementButton
          hasAgreedToCertification={hasAgreedToCertification}
          setHasAgreedToCertification={setHasAgreedToCertification}
        />
      </Box>

      <Box margin={{ top: 'spacing12' }} width="100%">
        <AppButton
          testId="claim-summary-footer_submit"
          onClick={handleSubmit}
          size="L"
          width="100%"
          disabled={isSubmitDisabled
            || isCreateClaimLoading
            || isCreateHsaWithdrawalClaimLoading 
            || !isClaimValidationSuccessfully 
            || hasUndeliverableAddressError}
        >
          {isCreateClaimLoading || isCreateHsaWithdrawalClaimLoading
            ? <Preloader color="white" />
            : t('Submit Claim')}
        </AppButton>
      </Box>

      <Text
        data-testId="claim-summary-footer_hint"
        margin={{ top: 'spacing12' }}
        size="small"
        weight={500}
        color={isPaymentMethodVerificationFailed ? 'danger' : 'textDisabled'}
      >
        {isPaymentMethodVerificationFailed
          ? t('Your payment method could not be verified. Please update your payment method to submit the claim.')
          : t('You must add all claim details to submit claim')}
      </Text>
    </Box>
  );
};

export default ClaimSummaryFooter;
